import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import type {
  MarketingRequest,
  MarketingRequestPatch,
  MarketingRequestComment,
  MarketingHubSettings,
  MarketingRequestType,
  MarketingRequestStatus,
  MarketingRequestPriority,
} from '../types/marketing';

const REQUESTS = 'marketingRequests';
const COMMENTS = 'marketingRequestComments';
const SETTINGS_DOC = 'settings/marketing_hub';

type FsRecord = Record<string, unknown>;

function fromRequestDoc(id: string, data: FsRecord): MarketingRequest {
  return {
    id,
    title: (data.title as string) || 'Untitled',
    description: (data.description as string | null) ?? null,
    requestType: (data.requestType as MarketingRequestType) || 'content',
    status: (data.status as MarketingRequestStatus) || 'open',
    priority: (data.priority as MarketingRequestPriority) || 'medium',
    dueDate: (data.dueDate as Timestamp | null) ?? null,
    requestedBy: (data.requestedBy as string) || '',
    requestedByName: (data.requestedByName as string) || '',
    assigneeUserId: (data.assigneeUserId as string) || '',
    linkedContentId: (data.linkedContentId as string | null) ?? null,
    createdAt: (data.createdAt as Timestamp) ?? Timestamp.now(),
    updatedAt: (data.updatedAt as Timestamp) ?? Timestamp.now(),
  };
}

function sanitizePatch(patch: MarketingRequestPatch): FsRecord {
  const out: FsRecord = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    if (v instanceof Date) {
      out[k] = Timestamp.fromDate(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function subscribeMarketingRequests(
  cb: (items: MarketingRequest[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(db, REQUESTS), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => fromRequestDoc(d.id, d.data()))),
    err => {
      firestoreSnapshotListenError('marketingRequestsService', REQUESTS, err);
      onError?.(err);
    },
  );
}

export function subscribeMarketingRequestComments(
  requestId: string,
  cb: (comments: MarketingRequestComment[]) => void,
): () => void {
  const q = query(
    collection(db, COMMENTS),
    where('requestId', '==', requestId),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(q, snap => {
    cb(
      snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.(),
        } as MarketingRequestComment;
      }),
    );
  });
}

export async function getMarketingHubSettings(): Promise<MarketingHubSettings> {
  const snap = await getDoc(doc(db, SETTINGS_DOC));
  if (!snap.exists()) return {};
  const data = snap.data();
  return { notifyUserId: (data.notifyUserId as string | null) ?? null };
}

export interface CreateMarketingRequestInput {
  title: string;
  description?: string | null;
  requestType: MarketingRequestType;
  priority: MarketingRequestPriority;
  dueDate?: Date | null;
  requestedBy: string;
  requestedByName: string;
  assigneeUserId: string;
  linkedContentId?: string | null;
}

export async function createMarketingRequest(
  input: CreateMarketingRequestInput,
): Promise<string> {
  const now = Timestamp.now();
  const settings = await getMarketingHubSettings();
  const assignee = settings.notifyUserId?.trim() || input.assigneeUserId;

  const payload: FsRecord = {
    title: input.title.trim() || 'Untitled',
    description: input.description?.trim() || null,
    requestType: input.requestType,
    status: 'open',
    priority: input.priority,
    dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : null,
    requestedBy: input.requestedBy,
    requestedByName: input.requestedByName,
    assigneeUserId: assignee,
    linkedContentId: input.linkedContentId ?? null,
    createdAt: now,
    updatedAt: now,
  };

  const ref = await addDoc(collection(db, REQUESTS), payload);
  return ref.id;
}

export async function updateMarketingRequest(
  id: string,
  patch: MarketingRequestPatch,
): Promise<void> {
  await updateDoc(doc(db, REQUESTS, id), {
    ...sanitizePatch(patch),
    updatedAt: Timestamp.now(),
  });
}

export async function deleteMarketingRequest(id: string): Promise<void> {
  await deleteDoc(doc(db, REQUESTS, id));
}

export interface CreateMarketingRequestCommentInput {
  requestId: string;
  userId: string;
  userName: string;
  userInitials?: string;
  text: string;
  bodyHtml?: string;
  mentions?: MarketingRequestComment['mentions'];
  attachments?: MarketingRequestComment['attachments'];
  parentId?: string | null;
  kind?: MarketingRequestComment['kind'];
}

export async function createMarketingRequestComment(
  input: CreateMarketingRequestCommentInput,
): Promise<string> {
  const payload: FsRecord = {
    requestId: input.requestId,
    userId: input.userId,
    userName: input.userName,
    userInitials: input.userInitials ?? null,
    text: input.text,
    bodyHtml: input.bodyHtml ?? null,
    bodyFormat: 'html',
    mentions: input.mentions ?? [],
    attachments: input.attachments ?? [],
    parentId: input.parentId ?? null,
    kind: input.kind ?? null,
    createdAt: Timestamp.now(),
  };
  const ref = await addDoc(collection(db, COMMENTS), payload);
  return ref.id;
}
